import React from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, Sparkles } from "lucide-react";

export function WidgetCard({ title, subtitle, action, children, className, testId, tone = "default" }) {
    return (
        <div
            data-testid={testId}
            className={cn(
                "flex flex-col rounded-md border bg-white",
                tone === "ai" ? "border-blue-200 bg-blue-50/40" : "border-zinc-200",
                className,
            )}
        >
            <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
                <div>
                    <div className="flex items-center gap-1.5">
                        {tone === "ai" && <Sparkles className="h-3 w-3 text-blue-500" strokeWidth={1.5} />}
                        <h3 className="text-[13px] font-semibold tracking-tight text-zinc-900">{title}</h3>
                    </div>
                    {subtitle && <p className="mt-0.5 text-[11px] text-zinc-500">{subtitle}</p>}
                </div>
                {action}
            </div>
            <div className="flex-1 p-4">{children}</div>
        </div>
    );
}

export function KpiCard({ label, value, delta, hint, testId, isPlaceholder = true }) {
    return (
        <div
            data-testid={testId}
            className="rounded-md border border-zinc-200 bg-white px-4 py-4 hover:border-zinc-300 transition-colors"
        >
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
            <div className="mt-2 flex items-baseline gap-2">
                {isPlaceholder ? (
                    <Skeleton className="h-7 w-24" />
                ) : (
                    <span className="font-display text-2xl font-semibold tracking-tight">{value}</span>
                )}
                {delta && (
                    <Badge variant="secondary" className="text-[10px] font-mono">
                        {delta}
                    </Badge>
                )}
            </div>
            <div className="mt-2 text-[11px] text-zinc-500">{hint || "No data yet"}</div>
        </div>
    );
}

export function ChartPlaceholder({ height = 200 }) {
    return (
        <div
            className="flex w-full items-end gap-1.5"
            style={{ height }}
            aria-hidden="true"
        >
            {Array.from({ length: 24 }).map((_, i) => (
                <div
                    key={i}
                    className="flex-1 rounded-sm bg-zinc-100"
                    style={{ height: `${20 + ((i * 37) % 70)}%` }}
                />
            ))}
        </div>
    );
}

export function EmptyList({ label = "No activity yet", hint = "Live events will appear here." }) {
    return (
        <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-zinc-200 py-10 text-center">
            <div className="text-sm font-medium text-zinc-700">{label}</div>
            <div className="mt-1 text-xs text-zinc-500">{hint}</div>
        </div>
    );
}

export function QuickActionButton({ icon: Icon, label, testId, onClick }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="group flex items-center justify-between gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-left hover:border-zinc-300 hover:bg-zinc-50 transition-colors"
        >
            <div className="flex items-center gap-2.5">
                {Icon && <Icon className="h-3.5 w-3.5 text-zinc-600" strokeWidth={1.5} />}
                <span className="text-[13px] font-medium text-zinc-800">{label}</span>
            </div>
            <ArrowUpRight className="h-3.5 w-3.5 text-zinc-400 group-hover:text-zinc-900 transition-colors" strokeWidth={1.5} />
        </button>
    );
}
